import { useAuth0 } from '@auth0/auth0-react';
import { useDebouncedFunction } from '@strata/helper-functions';
import { ChatWithConversationPane } from '@strata/react-chat';
import { SuccessToast } from '@strata/react-toast-message';
import { type ComponentProps, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { archiveNote, postMessage, softDeleteMessage } from 'api/medbillingAPI';
import { NewNoteDialog } from 'components/claims/dialogs/NewNoteDialog';
import { Permissions } from 'services/constants';
import { handleError } from 'services/Error';
import { useGetConversationByIdQuery, useGetNotesForClaimQuery } from 'store/api/medbillingApi';
import { usePermissionCheck } from 'store/auth/authHelpers';

export const NotesTab = () => {
    const { payerId, transactionId } = useParams() as { payerId: string; transactionId: string };
    const { user } = useAuth0();
    const writeMedBilling = usePermissionCheck(Permissions.WRITE_MED_BILLING);

    const [searchText, setSearchText] = useState<string>();
    const debouncedSearch = useDebouncedFunction();

    const {
        data: notes,
        isFetching: notesLoading,
        refetch: refetchNotes,
    } = useGetNotesForClaimQuery({
        payerId,
        transactionId,
        searchText,
    });

    const [selectedConversationId, setSelectedConversationId] = useState<string>();
    useEffect(() => {
        if (notes && notes.length > 0 && !selectedConversationId) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setSelectedConversationId(notes[0].conversationId);
        }
    }, [selectedConversationId, notes]);

    const {
        data: selectedConversation,
        isFetching: conversationLoading,
        refetch: refetchConversation,
    } = useGetConversationByIdQuery(
        {
            payerId,
            conversationId: selectedConversationId!,
        },
        { skip: !selectedConversationId },
    );

    const [showNewNoteDialog, setShowNewNoteDialog] = useState(false);
    const [chatLoading, setChatLoading] = useState(false);

    const onConversationDelete = useCallback<
        Required<ComponentProps<typeof ChatWithConversationPane>>['onConversationDelete']
    >(
        async (conversationId) => {
            try {
                setChatLoading(true);
                await archiveNote({
                    payerId,
                    conversationId,
                    details: {
                        first_name: user?.given_name,
                        last_name: user?.family_name,
                    },
                });
                SuccessToast({ message: 'Note deleted successfully.' });
                if (conversationId === selectedConversationId) {
                    setSelectedConversationId(undefined);
                }
                setChatLoading(false);
                await refetchNotes();
            } catch (err) {
                handleError('Unable to delete note.', err as Error);
            }
        },
        [payerId, user, refetchNotes, selectedConversationId],
    );

    const onMessageDelete = useCallback<
        Required<ComponentProps<typeof ChatWithConversationPane>>['onMessageDelete']
    >(
        async ({ messageId }) => {
            try {
                setChatLoading(true);
                await softDeleteMessage({
                    payerId,
                    messageId,
                    details: {
                        first_name: user?.given_name,
                        last_name: user?.family_name,
                    },
                });
                SuccessToast({ message: 'Message deleted successfully.' });
                setChatLoading(false);
                await refetchConversation();
            } catch (err) {
                handleError('Unable to delete message.', err as Error);
            }
        },
        [payerId, user, refetchConversation],
    );

    const onMessageSend = useCallback<
        ComponentProps<typeof ChatWithConversationPane>['onMessageSend']
    >(
        async ({ conversationId, details }) => {
            try {
                setChatLoading(true);
                await postMessage({
                    payerId,
                    conversationId,
                    details: {
                        content: { message: details.message },
                        first_name: user?.given_name,
                        last_name: user?.family_name,
                    },
                });
                SuccessToast({ message: 'Message saved successfully.' });
                setChatLoading(false);
                await refetchConversation();
            } catch (err) {
                handleError('Unable to save message.', err as Error);
            }
        },
        [payerId, user, refetchConversation],
    );

    return (
        <>
            <NewNoteDialog
                isOpen={showNewNoteDialog}
                payerId={payerId}
                transactionId={transactionId}
                onCancel={() => setShowNewNoteDialog(false)}
                onCreateSuccess={() => {
                    (async (newConversationId) => {
                        await refetchNotes();
                        setSelectedConversationId(newConversationId);
                        setShowNewNoteDialog(false);
                    })();
                }}
            />
            <ChatWithConversationPane
                conversations={notes}
                chatDisabled={!writeMedBilling}
                conversationType={ChatWithConversationPane.ConversationType.NOTES}
                highlightText={searchText}
                chatLoading={notesLoading || conversationLoading || chatLoading}
                onSearch={(value) =>
                    debouncedSearch(() => {
                        setSearchText(value || undefined);
                        setSelectedConversationId(undefined);
                    })
                }
                onConversationSelect={setSelectedConversationId}
                selectedConversation={selectedConversation}
                onNewConversationClick={() => setShowNewNoteDialog(true)}
                onConversationDelete={writeMedBilling ? onConversationDelete : undefined}
                onMessageDelete={writeMedBilling ? onMessageDelete : undefined}
                onMessageSend={onMessageSend}
            />
        </>
    );
};
